import { useEffect, useState } from "react";
import { DesignTokens, Table, Modal, Button, InfoIcon, CloseIcon, CheckIcon } from "melco-ui";
import { Pagination } from "antd";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { SELECTED_BG, SELECTED_ACCENT } from "./summitTheme";
import { LogsTableDiv } from "./summitStyled";

const { colors, spacings } = DesignTokens;

// Selectable page sizes — a single source of truth so the dropdown
// options and the default can't drift out of sync.
const PAGE_SIZE_OPTIONS = [10, 20, 50] as const;
const DEFAULT_PAGE_SIZE = PAGE_SIZE_OPTIONS[0];

// WHAT: a footer row that hosts antd's own Pagination control (numbered
// pages: 1 2 3 4 5 …), imported directly from `antd` rather than
// whatever melco-ui re-exports.
// WHY: two different attempts at wiring Table's *built-in* pagination
// (nested pagination.onChange, then Table's top-level onChange — both
// documented antd patterns) still didn't correctly advance pages, and
// melco-ui's own Pagination re-export was untested territory on top of
// that. Going straight to antd's Pagination — confirmed to be an actual
// dependency of this project via the `antd/lib/table/interface` types
// already used in this codebase's shared table hooks — removes a layer
// of uncertainty: it's a small, standalone component with no coupling
// to Table's own (buggy) internal pagination logic.
const PaginationFooter = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    padding: ${spacings.small}px ${spacings.middle}px;
`;

// Small bordered mono badge for the error code (e.g. WARN_UPDATE_PENDING),
// matching the reference screenshot's styling under the error message text.
//
// Colors are the same SELECTED_BG/SELECTED_ACCENT pair already used
// elsewhere in this feature for selected rows — confirmed legible against
// each other (sampled directly from a working reference screenshot), unlike
// the earlier #fff5f5/colors.main.negative combo which turned out to be
// unreadable once actually rendered.
const ErrorCodeBadge = styled.span`
    display: inline-block;
    margin-top: 3px;
    padding: 2px 8px;
    font-size: 11px;
    font-weight: 700;
    color: ${SELECTED_ACCENT};
    background: ${SELECTED_BG};
    border-radius: 4px;
`;

// WHAT: the confirmation screen's layout — icon, message, and the
// Continue button stacked and centered.
// WHY a dedicated container rather than reusing the iframe's bodyStyle
// centering as-is: the iframe is a single child centered on one row,
// but this step has three stacked elements, so it needs its own
// column-direction flex rather than the Modal's default row centering.
const WarningStep = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: ${spacings.small}px;
    padding: ${spacings.large}px;
    text-align: center;
`;

type LogsTableProps = {
    dataSource: any[];
    loading?: boolean;
};

// WHAT: centers whatever's passed as `children` both horizontally and
// vertically within the table cell.
// WHY a shared wrapper instead of inlining the flex styles at each
// return point: the Status column has more than one branch (success vs.
// everything else) that both need the same centering — a shared
// component means that alignment can't drift out of sync between them.
const CenteredCell = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
`;

export const LogsTable: React.FC<LogsTableProps> = ({ dataSource, loading }) => {
    const { t } = useTranslation();
    const [modalVisible, setModalVisible] = useState(false);
    const [modalContent, setModalContent] = useState<string | null>(null);
    // WHAT: whether the rep has clicked past the warning step for the
    // view that's currently open.
    // WHY reset on every openModal call rather than leaving it "sticky"
    // across views: each URL is a different log entry/config, so the
    // rep should see the warning again for each one rather than it being
    // silently skipped after the first time they confirm it.
    const [hasConfirmedWarning, setHasConfirmedWarning] = useState(false);

    // WHAT: current page + page size, owned entirely by us.
    // WHY plain state instead of anything shaped for Table's pagination
    // API: Table's own pagination is disabled below (`pagination={false}`),
    // so nothing here needs to match antd's TablePaginationConfig shape —
    // it only has to make sense to the standalone Pagination control
    // below, which we drive directly via its (page, pageSize) callback.
    const [current, setCurrent] = useState(1);
    const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);

    const total = dataSource.length;
    const pageCount = Math.max(1, Math.ceil(total / pageSize));

    // GOTCHA: if a status filter shrinks the data (or a newly selected
    // machine simply has fewer logs) while the rep is sitting on a later
    // page, that page can end up past the new last page. Clamp back to
    // the last valid page instead of showing an empty page.
    useEffect(() => {
        setCurrent((prev) => Math.min(prev, pageCount));
    }, [pageCount]);

    // Logs are fetched in one request rather than page-by-page from the
    // server, so we slice the current page out of the full array
    // ourselves and hand Table only that slice — Table never sees more
    // than `pageSize` rows at a time.
    const pagedData = dataSource.slice((current - 1) * pageSize, current * pageSize);

    // Pagination's onChange fires with the new page and page size
    // together, whether the rep clicked a page number or changed the
    // size — no need to distinguish the two cases here.
    const handlePaginationChange = (nextPage: number, nextPageSize: number) => {
        setCurrent(nextPage);
        setPageSize(nextPageSize);
    };

    const openModal = (url: string) => {
        setModalContent(url);
        setHasConfirmedWarning(false);
        setModalVisible(true);
    };

    const columns = [
        {
            title: ("Date/Time"),
            dataIndex: "created_date",
            key: "created_date",
            render: (_: string, record: any) => (
                <span style={{ fontSize: 13 }}>
                    {record.created_date ? new Date(record.created_date).toLocaleString() : ""}
                </span>
            ),
        },
        {
            title: ("Status"),
            key: "status",
            // WHAT: antd's native column alignment, applied to BOTH the
            // header cell and every body cell.
            // WHY this is the actual fix for "icon not aligned with the
            // Status title": the header ("Status") is left-aligned by
            // default, while only the cell *content* is centered via the
            // inner CenteredCell flex div — without `align`, the icon
            // centers itself within a cell whose header text hasn't
            // moved, producing a visible offset between them. `align`
            // centers the header and the cell the same way, so they're
            // guaranteed to line up regardless of column width.
            // GOTCHA: this got dropped the last time this column's render
            // was swapped over to melco-ui's own icons — if the Status
            // column ever gets edited again, make sure this stays attached.
            align: "center" as const,
            render: (rowObj: any) => {
                const status = rowObj?.status?.toString().toUpperCase();

                if (status === "ERROR") {
                    return (<CenteredCell><CloseIcon style={{ color: "red" }} /></CenteredCell>);
                }
                else if (status === "SUCCESS") {
                    return (<CenteredCell><CheckIcon style={{ color: "green" }} /></CenteredCell>);
                }
                else {
                    return (<CenteredCell>{status}</CenteredCell>);
                }
            }
        },
        {
            title: "Message",
            dataIndex: "message",
            key: "message",
            render: (_: string, record: any) => {
                const status = record?.status?.toString().toUpperCase();

                if (status === "ERROR") {
                    return (
                        <div style={{
                            fontSize: 13,

                        }}>
                            <div>{record?.error_message}</div>
                            {record?.error && <ErrorCodeBadge>{record.error}</ErrorCodeBadge>}
                        </div>
                    );
                }

                if (status === "SUCCESS") {
                    return <span style={{
                        fontSize: 13,

                        color: colors.gray.dark
                    }}>Scan Successful</span>;
                }

                return <span style={{
                    fontSize: 13,

                    color: colors.gray.dark
                }}>{record?.error_message ?? ""}</span>;
            },
        },
        {
            title: t("summit.table.columns.view"),
            key: "view",

            render: (_: string, record: any) =>
                record.url ? (
                    <Button type="primary" onClick={() => openModal(record.url)}>
                        View
                    </Button>
                ) : null,
        },
    ];

    return (
        <>
            <LogsTableDiv>
                <Table
                    columns={columns}
                    dataSource={pagedData}
                    // WHAT: Avoid using Table's built-in vertical scroll.
                    // WHY: The internal scrollbar takes up some horizontal space from the table body,
                    // reducing the available width for columns. This can make the table feel cramped
                    // and cause column widths to shrink. Keeping the table without internal scrolling
                    // allows it to use the full available width.
                    //  scroll={{ y: "44vh" }}
                    // WHAT: device_id + created_date + the row's position in
                    // the currently-rendered page.
                    // WHY the index is necessary here, not just decorative:
                    // device_id + created_date alone isn't guaranteed unique —
                    // multiple log entries can share the same device and the
                    // same timestamp (e.g. two events logged within the same
                    // second). Duplicate React keys among sibling rows cause
                    // React to misrender/reuse row instances between renders,
                    // which can look exactly like "pagination is broken" even
                    // when the underlying slicing logic is correct. There's no
                    // real unique id field on these records to key off instead,
                    // so the index — guaranteed unique within whatever page is
                    // currently rendered — is the safe fallback.
                    rowKey={(record: any, index?: number) =>
                        `${record.device_id}-${record.created_date}-${index}`
                    }
                    pagination={false}
                    loading={loading}
                />
            </LogsTableDiv>

            <PaginationFooter>
                <Pagination
                    current={current}
                    pageSize={pageSize}
                    total={total}
                    showSizeChanger
                    pageSizeOptions={PAGE_SIZE_OPTIONS.map(String)}
                    onChange={handlePaginationChange}
                    onShowSizeChange={handlePaginationChange}
                />
            </PaginationFooter>

            <Modal
                open={modalVisible}
                onCancel={() => setModalVisible(false)}
                footer={null}
                // WHAT: 90% of the viewport, but never wider than 800px.
                // WHY min() over a fixed "800px": on narrow viewports the
                // modal should still shrink to fit (90vw), it just stops
                // growing past 800px on wide screens — matching the
                // iframe's own maxWidth below instead of leaving empty
                // space around a narrower iframe inside a wider modal.
                width={!hasConfirmedWarning ? "auto" : "min(90%, 800px)"}
                bodyStyle={{
                    padding: 0,
                    height: hasConfirmedWarning ? "95vh" : undefined,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                }}
                closable={true}
                destroyOnClose={true}
                maskClosable={false}
                centered
            >
                {!hasConfirmedWarning ? (
                    <WarningStep>
                        <InfoIcon style={{ fontSize: "32px" }} />
                        <span style={{ fontSize: 14, color: colors.gray.dark }}>
                            View will only work if customer didn't delete the configuration
                        </span>
                        <Button type="primary" onClick={() => setHasConfirmedWarning(true)}>
                            Continue
                        </Button>
                    </WarningStep>
                ) : (
                    modalContent && (
                        <iframe
                            src={modalContent}
                            style={{
                                width: "100%",
                                maxWidth: 800,
                                height: "100%",
                                border: "none",
                            }}
                        />
                    )
                )}
            </Modal>
        </>
    );
};
